import { inject, injectable, interfaces } from 'inversify';
import { action, makeObservable } from 'mobx';
import { IConfiguration } from '../../../../configuration/config.types';
import { BaseHubService } from '../../../services/baseHub.service';
import { IAuthStore } from '../../auth/auth.store';
import { IHubNotification, NotificationType } from '../models/notification.model';
import { INotificationsStore } from '../stores/notifications.store';

export interface INotificationHubService {
  initializeAsync(): Promise<void>;
  removeNotificationsAsync(id: string[]): Promise<void>;
  markAsReadNotificationsAsync(ids: string[]): Promise<void>;
  pushNotificationAsync(notification: IHubNotification): Promise<void>;
}

export namespace INotificationHubService {
  export const $: interfaces.ServiceIdentifier<INotificationHubService> = Symbol('NOTIFICATION_HUB_SERVICE');
}

const HUB_METHODS = {
  PUSH_NOTIFICATION: 'PushNotificationAsync',
  REMOVE_NOTIFICATIONS: 'RemoveNotificationsAsync',
  MARK_AS_READ_NOTIFICATIONS: 'MarkAsReadNotificationsAsync',
};

const HUB_HANDLERS = {
  NOTIFICATIONS_REMOVED: 'RemoveNotifications',
  NOTIFICATION_PUSHED: 'PushNotification',
  NOTIFICATIONS_PUSHED: 'PushNotifications',
  NOTIFICATIONS_MARKED_AS_READ: 'MarkAsReadNotifications',
};

interface IPushNotificationPayload {
  type: NotificationType;
  notification: string;
}

@injectable()
export class NotificationHubService extends BaseHubService implements INotificationHubService {
  constructor(
    @inject(IConfiguration.$) config: IConfiguration,
    @inject(IAuthStore.$) auth: IAuthStore,
    @inject(INotificationsStore.$) private readonly notificationStore: INotificationsStore
  ) {
    super(auth, config.hubUrl);
    makeObservable(this);
    this.addHandlers();
  }

  addHandlers(): void {
    this.connection.on(HUB_HANDLERS.NOTIFICATION_PUSHED, this.handleNotificationPushed);
    this.connection.on(HUB_HANDLERS.NOTIFICATIONS_REMOVED, this.handleNotificationsRemoved);
    this.connection.on(HUB_HANDLERS.NOTIFICATIONS_PUSHED, this.handleNotificationsPushed);
    this.connection.on(HUB_HANDLERS.NOTIFICATIONS_MARKED_AS_READ, this.handleNotificationsMarkedAsRead);
  }

  async pushNotificationAsync(notification: IHubNotification): Promise<void> {
    await this.connectAsync();
    await this.connection.send(HUB_METHODS.PUSH_NOTIFICATION, {
      type: notification.type,
      notification: JSON.stringify(notification),
    } as IPushNotificationPayload);
  }

  async removeNotificationsAsync(ids: string[]): Promise<void> {
    await this.connectAsync();
    await this.connection.send(HUB_METHODS.REMOVE_NOTIFICATIONS, ids);
  }

  async markAsReadNotificationsAsync(ids: string[]): Promise<void> {
    await this.connectAsync();
    await this.connection.send(HUB_METHODS.MARK_AS_READ_NOTIFICATIONS, ids);
  }

  @action
  handleNotificationPushed = (hubNotification: IHubNotification): void => {
    if (!this.notificationStore.notifications.some((x) => x.id === hubNotification.id)) {
      this.notificationStore.notifications.push(hubNotification);
    }
  };

  @action
  handleNotificationsRemoved = (ids: string[]): void => {
    this.notificationStore.notifications.replace(this.notificationStore.notifications.filter((item) => !ids.includes(item.id)));
  };

  @action
  handleNotificationsPushed = (hubNotifications: IHubNotification[]): void => {
    const newNotifications = hubNotifications.filter((x) => !this.notificationStore.notifications.some((n) => n.id === x.id));
    this.notificationStore.notifications.push(...newNotifications);
  };

  @action
  handleNotificationsMarkedAsRead = (ids: string[]): void => {
    ids.forEach((i: string) => {
      const notification = this.notificationStore.notifications.find((n) => n.id === i);
      if (notification) {
        notification.wasRead = true;
      }
    });
  };
}
